import { AnyPlainObject } from "../common/misc";

export type GeneralServiceArgType = { service: string; data: any };

export type SpotServiceArgType = {
  cmd: string;
  data: any;
};

export type CustomActionServiceArgType = {
  service: string;
  [key: string]: any;
};

export class ServiceCaller {
  callService: <ReqType, ResType>(
    service: string,
    data: ReqType,
    options?: { serviceType?: string; timeoutMs?: number }
  ) => Promise<ResType> = () => {
    throw Error("`Service`'s callService is not meant to be called");
  };
  callSpotService: <ReqType, ResType>(
    cmd: string,
    data: ReqType
  ) => Promise<ResType> = () => {
    throw Error("`Service`'s callSpotService is not meant to be called");
  };
  callCustomActionService: <ReqType extends AnyPlainObject, ResType>(
    service: string,
    args: ReqType
  ) => Promise<ResType> = () => {
    throw Error(
      "`Service`'s callCustomActionService is not meant to be called"
    );
  };
}

export type CheckResponseOrThrow<ResType> = (response: ResType) => ResType;

export interface Protocol<ResponseType> {
  [key: string]: {
    makeReq: (...args: any[]) => GeneralServiceArgType;
    transformRes: (response: ResponseType) => any;
  };
}

export type Services<
  ResponseType,
  ProtocolType extends Protocol<ResponseType>
> = {
  [key in keyof ProtocolType]: (
    ...args: Parameters<ProtocolType[key]["makeReq"]>
  ) => Promise<ReturnType<ProtocolType[key]["transformRes"]>>;
};

export type ServicesFactory<
  ResponseType,
  ProtocolType extends Protocol<ResponseType>
> = (service: ServiceCaller) => Services<ResponseType, ProtocolType>;

export type ServicesFromProtocols = <
  ResponseType,
  ProtocolType extends Protocol<ResponseType>
>(
  protocol: ProtocolType,
  checkResponseOrThrow: CheckResponseOrThrow<ResponseType>
) => ServicesFactory<ResponseType, ProtocolType>;

export const servicesFromProtocols: ServicesFromProtocols =
  <ResponseType, ProtocolType extends Protocol<ResponseType>>(
    protocol: ProtocolType,
    checkResponseOrThrow: CheckResponseOrThrow<ResponseType>
  ) =>
  (serviceCaller: ServiceCaller) => {
    const generatedService = {} as ReturnType<
      ServicesFactory<ResponseType, ProtocolType>
    >;
    for (const s in protocol) {
      const serviceName = s as keyof ProtocolType;
      generatedService[serviceName] = async (...args: any[]) => {
        const makeReq = protocol[serviceName].makeReq;
        const req = makeReq(...args);
        const res = await serviceCaller.callService<any, ResponseType>(
          req.service,
          req.data
        );
        checkResponseOrThrow(res);
        return protocol[serviceName].transformRes(res);
      };
    }
    return generatedService as any;
  };
